/**
 * fp-hash.js — shared device-fingerprint hashing (runs on client AND server).
 *
 * SINGLE SOURCE OF TRUTH for turning raw device `signals` into ids. The client
 * (device-fp.js) collects signals and computes these ids for its own cache; the
 * server RE-DERIVES them from the posted signals and IGNORES the client's ids —
 * never trust a client-sent id, only the signals it was built from.
 *
 * KEEP THIS FILE BYTE-IDENTICAL to the backend copy at
 * sohozkaj-backend/src/lib/fp-hash.js. If they diverge, the client and server
 * compute different ids and every fingerprint stops matching. If you change any
 * hash function (or the font handling) you MUST bump FP_VERSION in BOTH copies.
 *
 * Do NOT fork this file. Import it on both sides.
 */

export const FP_VERSION = 2;

/* ------------------------------------------------------------------ */
/* Hashing                                                             */
/* ------------------------------------------------------------------ */

const hex32 = (n) => (n >>> 0).toString(16).padStart(8, '0');

/** FNV-1a dual-seed -> [uint32, uint32] */
function hashPair(str) {
    let a = 0x811c9dc5, b = 0xcbf29ce4;
    for (let i = 0; i < str.length; i++) {
        const c = str.charCodeAt(i);
        a = Math.imul(a ^ c, 0x01000193) >>> 0;
        b = Math.imul(b ^ c, 0x85ebca6b) >>> 0;
    }
    return [a >>> 0, b >>> 0];
}

const hash64 = (str) => { const [a, b] = hashPair(str); return hex32(a) + hex32(b); };

/** SimHash — locality sensitive; similar font sets -> small Hamming distance. */
function simhash64(items) {
    if (!items.length) return '0'.repeat(16);
    const v = new Int32Array(64);
    for (const it of items) {
        const [a, b] = hashPair(it);
        for (let i = 0; i < 32; i++) {
            v[i] += (a >>> i) & 1 ? 1 : -1;
            v[32 + i] += (b >>> i) & 1 ? 1 : -1;
        }
    }
    let lo = 0, hi = 0;
    for (let i = 0; i < 32; i++) {
        if (v[i] > 0) lo |= (1 << i);
        if (v[32 + i] > 0) hi |= (1 << i);
    }
    return hex32(hi) + hex32(lo);
}

/* ------------------------------------------------------------------ */
/* Normalisation                                                       */
/* ------------------------------------------------------------------ */

/** Efficiency-core reporting differs across browsers on the same CPU. Bucket it.
 *  Idempotent: bucketing an already-bucketed value returns it unchanged. */
export function coreBucket(n) {
    if (!n || n < 1) return 0;
    if (n <= 2) return 2;
    if (n <= 4) return 4;
    if (n <= 8) return 8;
    if (n <= 16) return 16;
    if (n <= 32) return 32;
    return 64;
}

/* ------------------------------------------------------------------ */
/* Public API                                                          */
/* ------------------------------------------------------------------ */

/**
 * Recompute all ids from raw signals. The server calls this on posted signals and
 * uses the result, discarding any client-sent ids.
 *
 *   coreId  — stable, low-entropy hardware bucket (screen, depth, cores, memory,
 *             touch). A DB bucket key, never an identity by itself.
 *   fullId  — coreId + exact sorted font list. Exact-match fast path (same
 *             device + same browser).
 *   fontSim — SimHash of the font list ONLY. Independent of screen size /
 *             resolution / browser window.
 *   bands   — 4 chunks of fontSim for optional LSH lookup.
 */
export function buildIds(signals) {
    const screen = String(signals.screen ?? '0x0');
    const depth = Number(signals.depth) || 0;
    // Re-bucket from raw cores when present so a spoofed/odd `cores` can't drift the id.
    const cores = coreBucket(signals.coresRaw ?? signals.cores);
    const memory = Number(signals.memory) || 0;
    const touch = Number(signals.touch) || 0;
    const fonts = Array.isArray(signals.fonts) ? [...signals.fonts].sort() : [];

    const coreCanon = `${FP_VERSION}|${screen}|${depth}|${cores}|${memory}|${touch}`;
    const coreId = hash64(coreCanon);
    const fullId = hash64(`${coreCanon}|${fonts.join(',')}`);
    const fontSim = simhash64(fonts);
    const bands = [0, 4, 8, 12].map((i, n) => `${n}:${fontSim.slice(i, i + 4)}`);

    return { coreId, fullId, fontSim, bands };
}

/**
 * Jaccard similarity between two font lists: |A ∩ B| / |A ∪ B|, in [0, 1].
 * 1 = identical sets, 0 = no overlap (or either side empty → no signal).
 */
export function jaccard(a, b) {
    const A = new Set(Array.isArray(a) ? a : []);
    const B = new Set(Array.isArray(b) ? b : []);
    if (A.size === 0 || B.size === 0) return 0;
    let inter = 0;
    for (const x of A) if (B.has(x)) inter++;
    const union = A.size + B.size - inter;
    return union === 0 ? 0 : inter / union;
}

/** Hamming distance between two 16-hex (64-bit) SimHashes. 0 = identical, 64 = max. */
export function hamming(a, b) {
    if (typeof a !== 'string' || typeof b !== 'string' || a.length !== 16 || b.length !== 16) return 64;
    let d = 0;
    for (let k = 0; k < 16; k += 8) {
        let x = (parseInt(a.slice(k, k + 8), 16) ^ parseInt(b.slice(k, k + 8), 16)) >>> 0;
        while (x) { x &= x - 1; d++; }
    }
    return d;
}

/** Shape/size guard for untrusted network input. Rejects anything buildIds can't trust. */
export function isValidSignals(s) {
    if (!s || typeof s !== 'object') return false;
    if (typeof s.screen !== 'string' || s.screen.length > 20) return false;
    if (!Array.isArray(s.fonts) || s.fonts.length > 128) return false;
    for (const f of s.fonts) {
        if (typeof f !== 'string' || f.length > 64) return false;
    }
    for (const k of ['depth', 'cores', 'coresRaw', 'memory', 'touch']) {
        if (s[k] != null && typeof s[k] !== 'number') return false;
    }
    return true;
}
